import { TranscriptionJob, TranscriptionStatus } from '../types';

const SESSION_JOBS_KEY = 'transcriptionSessionJobs';
const SETTINGS_KEY = 'echoScribeSettings';

// Files above this threshold are uploaded to Firebase Storage; smaller files stored as base64 in Firestore
export const STORAGE_THRESHOLD_BYTES = 750 * 1024; // 750 KB

// Exponential backoff delays in ms: 5s → 15s → 45s
export const RETRY_BACKOFF_DELAYS = [5000, 15000, 45000];
export const MAX_RETRIES = 3;

export const getRetryDelay = (retryCount: number): number => {
  return RETRY_BACKOFF_DELAYS[Math.min(retryCount, RETRY_BACKOFF_DELAYS.length - 1)];
};

export interface AppSettings {
  disableFallback: boolean;
}

export const getSettings = (): AppSettings => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...{ disableFallback: false }, ...JSON.parse(raw) } : { disableFallback: false };
  } catch {
    return { disableFallback: false };
  }
};

export const saveSettings = (settings: AppSettings): void => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

// A version of the job that is safe to store in JSON, omitting the non-serializable File object
export type StorableJob = Omit<TranscriptionJob, 'file'>;

export const setSessionJobs = (jobs: TranscriptionJob[]): void => {
  if (typeof window.localStorage === 'undefined') return;
  // Omit the 'file' property before storing
  const storableJobs: StorableJob[] = jobs.map(({ file, ...rest }) => rest);
  localStorage.setItem(SESSION_JOBS_KEY, JSON.stringify(storableJobs));
};

export const getSessionJobs = (): StorableJob[] => {
  if (typeof window.localStorage === 'undefined') return [];
  try {
    const jobsJson = localStorage.getItem(SESSION_JOBS_KEY);
    return jobsJson ? JSON.parse(jobsJson) : [];
  } catch (e) {
    console.error('Failed to parse session jobs', e);
    return [];
  }
};

export const clearSessionJobs = (): void => {
    if (typeof window.localStorage === 'undefined') return;
    localStorage.removeItem(SESSION_JOBS_KEY);
};

export const removeSessionJobsByIds = (ids: string[]): void => {
  if (typeof window.localStorage === 'undefined') return;
  const idSet = new Set(ids);
  const remaining = getSessionJobs().filter(j => !idSet.has(j.id));
  localStorage.setItem(SESSION_JOBS_KEY, JSON.stringify(remaining));
};
